import services from '../services'
import * as types from './mutation-types'
import { errorMessage } from '@/components/alertsMessages'

export const GetJourneys = ({ commit }, params) => (
  services.list(params).then(res => {
    commit(types.SET_JOURNEYS, res.data)
  })
)

export const GetJourney = async ({ commit }, id) => {
  commit(types.SET_LOADING, true)
  try {
    const res = await services.show(id)
    commit(types.SET_JOURNEY, res.data.data)
    commit(types.SET_DIRTY, false)
  } finally {
    commit(types.SET_LOADING, false)
  }
}

export const SaveJourney = async ({ commit, state }) => {
  const journey = state.journey
  if (!journey || !journey.name || !journey.name.trim()) {
    throw new Error('Insira o nome da jornada.')
  }

  commit(types.SET_SAVING, true)
  try {
    // Chave local de cada nó, na MESMA ordem do payload. É por ela que os ids
    // criados pelo servidor voltam para os nós certos, sem depender do objeto
    // (que pode ter sido reeditado enquanto o request estava em voo).
    const sentKeys = state.nodes.map(node => node.id || node._tempId)
    const revisionAtSave = state.revision

    const nodesPayload = state.nodes.map((node, index) => {
      let parentNodeId = node.parent_node_id || null

      // Resolve temp parent IDs to position references for the backend
      if (parentNodeId && typeof parentNodeId === 'string' && parentNodeId.startsWith('temp_')) {
        const parentNode = state.nodes.find(n => (n.id || n._tempId) === parentNodeId)
        if (parentNode && parentNode.id) {
          parentNodeId = parentNode.id
        } else if (parentNode) {
          const parentIndex = state.nodes.indexOf(parentNode)
          parentNodeId = '_pos:' + parentIndex
        }
      }

      return {
        id: node.id || undefined,
        type: node.type,
        kind: node.kind,
        label: node.label,
        description: node.description,
        config: node.config && !Array.isArray(node.config) ? node.config : {},
        position: index,
        parent_node_id: parentNodeId,
        branch_type: node.branch_type || null
      }
    })

    const payload = {
      name: journey.name.trim(),
      description: journey.description || '',
      nodes: nodesPayload
    }

    const res = await services.save(journey.id || null, payload)

    // Se algo foi editado enquanto o request viajava, a resposta já nasceu
    // desatualizada: preserva o estado local e mantém `dirty` para reagendar.
    const reconcile = state.revision !== revisionAtSave
    commit(types.APPLY_SAVE_RESULT, { journey: res.data.data, sentKeys, reconcile })
    commit(types.SET_DIRTY, reconcile)
    return res
  } finally {
    commit(types.SET_SAVING, false)
  }
}

export const DeleteJourney = async ({ commit }, id) => {
  await services.remove(id)
}

export const DuplicateJourney = async ({ commit }, id) => {
  const res = await services.duplicate(id)
  return res.data.data
}

export const ChangeStatus = async ({ commit, state }, { id, status }) => {
  const res = await services.changeStatus(id, { status })
  if (state.journey && state.journey.id === id) {
    commit(types.SET_JOURNEY, { ...state.journey, status })
  }
  return res
}

export const AddNode = ({ commit }, node) => {
  if (!node._tempId) {
    node._tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }
  commit(types.ADD_NODE, node)
}

export const UpdateNode = ({ commit }, node) => {
  commit(types.UPDATE_NODE, node)
}

export const RemoveNode = ({ commit }, nodeId) => {
  commit(types.REMOVE_NODE, nodeId)
}

export const ReorderNodes = ({ commit, state }, nodes) => {
  const branchNodes = state.nodes.filter(n => n.parent_node_id)
  commit(types.SET_NODES, [...nodes, ...branchNodes])
  commit(types.SET_DIRTY, true)
}

export const SelectNode = ({ commit }, nodeId) => {
  commit(types.SET_SELECTED_NODE, nodeId)
}

export const UpdateJourneyField = ({ commit, state }, { field, value }) => {
  if (state.journey) {
    commit(types.SET_JOURNEY, { ...state.journey, [field]: value })
    commit(types.SET_DIRTY, true)
  }
}

export const LoadReferenceData = async ({ commit }) => {
  // Antes, qualquer endpoint que falhasse virava [] em silêncio — e um select sem
  // options é visualmente idêntico a "a configuração se perdeu". Agora a chave que
  // falha fica `undefined` (a mutation preserva o valor anterior) e o usuário é avisado.
  const calls = [
    { key: 'emailTemplates', label: 'templates de e-mail', run: () => services.getEmailTemplates() },
    { key: 'waTemplates', label: 'templates de WhatsApp', run: () => services.getWaTemplates() },
    { key: 'statuses', label: 'status', run: () => services.getStatuses() },
    { key: 'groups', label: 'grupos', run: () => services.getGroups() },
    { key: 'tags', label: 'tags', run: () => services.getTags() },
    { key: 'forms', label: 'formulários', run: () => services.getForms({ limit: 100 }) },
    { key: 'journeysList', label: 'jornadas', run: () => services.getJourneysList() },
    { key: 'fieldTypes', label: 'campos', run: () => services.getFieldTypes() },
    { key: 'refStatuses', label: 'motivos de pendência', run: () => services.getRefStatuses(), shape: 'object' },
    { key: 'formConditionFields', label: 'campos de condição', run: () => services.getConditionFields() },
    { key: 'followUpRules', label: 'ações de retorno', run: () => services.getFollowUpRules() }
  ]

  const settled = await Promise.allSettled(calls.map(c => c.run()))

  const payload = {}
  const failed = []

  settled.forEach((res, i) => {
    const { key, label, shape } = calls[i]
    if (res.status !== 'fulfilled') {
      failed.push(label)
      return
    }
    const body = res.value && res.value.data
    const value = (body && body.data !== undefined) ? body.data : body
    const ok = shape === 'object'
      ? (value && typeof value === 'object')
      : Array.isArray(value)
    if (!ok) {
      failed.push(label)
      return
    }
    payload[key] = value
  })

  commit(types.SET_REFERENCE_DATA, payload)

  if (failed.length) {
    errorMessage(`Não foi possível carregar: ${failed.join(', ')}. Alguns campos do editor podem aparecer vazios — recarregue a página antes de salvar.`)
  }
}

export const GetAnalytics = async ({ commit }, id) => {
  try {
    const [metricsRes, funnelRes, entriesRes, nodeStatsRes] = await Promise.allSettled([
      services.getMetrics(id),
      services.getFunnel(id),
      services.getEntries(id, { days: 30 }),
      services.getNodeStats(id)
    ])

    commit(types.SET_ANALYTICS, {
      metrics: metricsRes.status === 'fulfilled' ? metricsRes.value.data.data : {},
      funnel: funnelRes.status === 'fulfilled' ? funnelRes.value.data.data : [],
      entries: entriesRes.status === 'fulfilled' ? entriesRes.value.data.data : [],
      nodeStats: nodeStatsRes.status === 'fulfilled' ? nodeStatsRes.value.data.data : []
    })
  } catch (e) {
    console.error('Failed to load analytics', e)
  }
}

export const GetLogs = async ({ commit }, { id, params }) => {
  try {
    const res = await services.getLogs(id, params)
    commit(types.SET_ANALYTICS, { logs: res.data })
  } catch (e) {
    console.error('Failed to load logs', e)
  }
}
